import type { ReactNode, SVGAttributes } from 'react';

/**
 * Props comunes para todos los iconos simbolicos del brand kit.
 *
 * - `size`: dimension del SVG en px (o string, ej. "1em"). Default 24.
 * - `strokeWidth`: grosor de trazo. Default 1.5 para coincidir con la familia tecnica (lucide).
 * - `className`: utilidades Tailwind (color via `text-*`, layout via `w-* h-*`, etc.).
 * - `aria-label`: si se provee, el icono se considera informativo y se expone con role="img".
 *   Si no, queda como decorativo (`aria-hidden="true"`).
 */
export interface IconProps {
  size?: number | string;
  className?: string;
  strokeWidth?: number;
  'aria-label'?: string;
}

/**
 * Props internas que recibe el wrapper SVG. Extiende SVGAttributes para
 * permitir cualquier paso adicional sin perder type-safety.
 */
interface IconBaseProps extends IconProps {
  children: ReactNode;
  /** ViewBox custom (en casos raros). Default "0 0 24 24". */
  viewBox?: string;
}

/**
 * Componente base de la iconografia simbolica.
 *
 * Centraliza:
 * - viewBox 0 0 24 24 (estandar del catalogo).
 * - fill="none", stroke="currentColor" — el color final lo decide el padre.
 * - strokeLinecap/Linejoin "round" para coherencia estetica.
 * - manejo de aria-hidden vs aria-label.
 *
 * No debe usarse directamente desde la app; es soporte de los iconos del set.
 */
export function IconBase({
  size = 24,
  className,
  strokeWidth = 1.5,
  viewBox = '0 0 24 24',
  children,
  'aria-label': ariaLabel,
  ...rest
}: IconBaseProps & Omit<SVGAttributes<SVGSVGElement>, keyof IconBaseProps>) {
  // Si recibe aria-label, el icono es informativo; si no, decorativo.
  const isLabelled = typeof ariaLabel === 'string' && ariaLabel.length > 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={isLabelled ? 'img' : undefined}
      aria-label={isLabelled ? ariaLabel : undefined}
      aria-hidden={isLabelled ? undefined : true}
      focusable={false}
      {...rest}
    >
      {children}
    </svg>
  );
}
